import { Check, ContentCopy, Delete, Edit, FilterAltOutlined as FilterAltOutlinedIcon, Rule } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  buildTestCycleFilterConfig,
  TEST_CYCLE_FILTER_DEFAULTS,
} from '../../../features/testcycles/Testcyclefilterconfig';
import TestCycleTableActions from '../../../features/testcycles/TestCycleTableActions';
import { useNotification } from '../../../hooks/NotificationContext';
import { useUser } from '../../../context/UserContext';
import useDebounce from '../../../hooks/useDebounce';
import useExecutionScope from '../../../hooks/useExecutionScope';
import { useFilterState } from '../../../hooks/useFilterState';
import { createQueryString } from '../../../lib/utils';
import { getFilterPresets } from '../../../modules/datafilters';
import { completeTestCycle } from '../../../modules/testcycles';
import {
  useJenkinsJobs,
  useProjectUsers,
  useTestCycleConfig,
  useTestCycles,
} from '../../../query/apiQueries';
import { ActionButton, AppliedFilterChips, FilterModal } from '../../../ui/data/filters';
import TestCycleTable from '../../../ui/data/table/TableComponent';
import ActionMenu from '../../../ui/primitives/ActionMenu';
import ConfirmationModal from '../../../ui/primitives/ConfirmationModal';
import SearchBar from '../../../ui/primitives/SearchBar';
import ExecutionScopeForm from '../../elements/common/execution/ExecutionScopeForm';
import { tableHeaderTestCycles } from '../test-case/testCaseTableHead';
import AddTestCycleModal from './AddTestCycleModal';
import { formatData } from './static-data';
import { useAccess } from '../../../hooks/useAccess';
import { Guard } from '../../../features/Guard';

const DEFAULT_COLUMNS = [
  'key',
  'name',
  'type',
  'status',
  'totalAssociatedTestCases',
  'totalIterations',
  'progress',
  'lastUpdated',
];

const TestCyclesTabContentComponent = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const [showModal, setShowModal] = useState(false);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [selected, setSelected] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [searchValue, setSearchValue] = useState(location.state?.searchValue || '');
  const [page, setPage] = useState(location.state?.page || 1);
  const debouncedSearchQuery = useDebounce(searchValue, 500);
  const { isScopeDefined } = useExecutionScope(projectId);
  const filterConfig = useMemo(() => buildTestCycleFilterConfig(), []);
  const [savedFilters, setSavedFilters] = useState([]);
  const {currentUser} = useUser();
  const [showFirstConfirm, setShowFirstConfirm] = useState(false);
  const [showSecondConfirm, setShowSecondConfirm] = useState(false);
  const [selectedRowForComplete, setSelectedRowForComplete] = useState(null);

  const { canAccess, activeRole } = useAccess();
  const canManageTestCycles = canAccess('ManageTestCycles');
  const canDeleteTestCycles = canAccess('DeleteTestCycles');
  const {
    filters,
    setFilters,
    removeFilter,
    clearAllFilters,
    activeCount,
  } = useFilterState(filterConfig, location.state?.filters || TEST_CYCLE_FILTER_DEFAULTS);

  const { data: config = [], isLoading: loadingConfig } = useTestCycleConfig(projectId);
  const { data: projectUsers = [], isLoading: loadingUsers } = useProjectUsers(projectId);
  const { data: jobs = [], isLoading: loadingJobs, error: jobError } = useJenkinsJobs(projectId);
  const [pendingAction, setPendingAction] = useState(null);

  const optionsMap = useMemo(() => ({
    status: config?.status || [],
    owner: projectUsers.map(u => ({ id: u.id, name: u.full_name })),
    purpose: config?.purpose || [],
    type: config?.type || [],
  }), [config, projectUsers]);

  const [perPage, setPerPage] = useState(location.state?.perPage || 10);
  const queryString = createQueryString(filters);

  const {
    data: testCycleResponse,
    isLoading: loadingCycles,
    isFetching: isFetchingCycles,
    refetch,
    error: testCycleError,
  } = useTestCycles(
    {
      page,
      perPage,
      search: debouncedSearchQuery,
      queryString,
      projectId,
      sortBy: orderBy,
      sortOrder: order,
    }
  );

  const testcycles = testCycleResponse?.testcycles || [];
  const totalCount = testCycleResponse?.total || 0;
  const isInitialLoading = loadingCycles || loadingUsers || loadingJobs || loadingConfig;

  useEffect(() => {
    if (testCycleError) {
      addNotification('danger', testCycleError.message || 'Failed to fetch test cycles. Please try again later.');
    }
  }, [testCycleError]);

  useEffect(() => {
    navigate('.', { replace: true, state: { filters, searchValue, page, perPage } });
  }, [debouncedSearchQuery, filters, page, perPage, navigate]);

  const STORAGE_KEY = `table-columns-testcycles-${projectId}`;
  const [selectedColumns, setSelectedColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const parsed = saved ? JSON.parse(saved) : null;
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_COLUMNS;
    } catch {
      return DEFAULT_COLUMNS;
    }
  });
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedColumns));
  }, [selectedColumns]);

  const formattedData = useMemo(() => formatData(testcycles), [testcycles]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setPage(1);
  };

  const handleRefresh = () => {
    refetch();
    setSelected([]);
    setSelectedRows([]);
  }

  const handlePageSizeChange = (e) => {
  const newPerPage = parseInt(e.target.value, 10);
  setPerPage(newPerPage);
  setPage(1);
};

  const handleSortChange = (key, direction) => {
    setOrderBy(key);
    setOrder(direction);
    setPage(1);
  };

  const handleClearAllFilters = () => {
    setSearchValue('');
    setPage(1);
    clearAllFilters();

   navigate('.', {
      replace: true,
      state: {
        filters: TEST_CYCLE_FILTER_DEFAULTS,
        searchValue: '',
        page: 1,
        perPage,
        showArchived: false,
      },
    });
  };

  const fetchFilterPresets = async () => {
    if (!projectId) return;
    try {
      const response = await getFilterPresets(projectId, 'testcycle');
      const normalized = (response?.data || []).map((preset) => {
          return {
            ...preset,
            createdBy: preset.user_id,
            shareLevel: preset.share_level,
          };
      });
      setSavedFilters(normalized);
    } catch (error) {
      console.error('Failed to fetch filter presets:', error);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchFilterPresets();
    }
  }, [projectId]);

  const handleEdit = (row) => {
    navigate(`/project/${projectId}/testcycles/${row.key}?type=details`, { state: { testCycleId: row.id, isEdit: true } });
  };

  const handleClone = (row) => setPendingAction({ type: 'clone', row });

  const handleDelete = (row) => setPendingAction({ type: 'delete', row });

  const handleMarkCompleteClick = (row) => {
    setSelectedRowForComplete(row);
    setShowFirstConfirm(true);
  };

  const handleCompleteTestCycle = async () => {
    try {
      await completeTestCycle(selectedRowForComplete.id);
      refetch();
      addNotification('success', 'Test cycle has been marked as completed!');
    } catch (error) {
      addNotification(
        'danger',
        error?.message || 'Failed to update test cycle.'
      );
    }
  };

  return (
    <>
      <Container fluid className="mt-2">
        <Row>
          <Col>
            <Typography variant="h4" className="mb-3">Test Cycles</Typography>
          </Col>
        </Row>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          wrap="wrap"
          gap={1}
        >
          <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchBar
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
              clearSearch={() => handleSearch('')}
              placeholder="Search Test Cycles"
            />
            <ActionButton
              onClick={() => setShowFilterModal(true)}
              label="Filters"
              icon={<FilterAltOutlinedIcon />}
              badgeContent={activeCount}
            />
          </Grid>
          <Guard policy="ManageTestCycles">
            <TestCycleTableActions
              handleOpenTestCycleModal={() => setShowModal(true)}
              isSelectedItems={selectedRows?.length}
              selectedItems={selected}
              refreshData={handleRefresh}
              selectedRows={selectedRows}
              addNotification={addNotification}
              pendingAction={pendingAction}
              setPendingAction={setPendingAction}
            />
          </Guard>
        </Grid>
        {(activeCount > 0 || searchValue) && (
          <Box sx={{ mt: 2 }}>
            <AppliedFilterChips
              filters={filters}
              filterConfig={filterConfig}
              optionsMap={optionsMap}
              onRemove={removeFilter}
              onClearAll={handleClearAllFilters}
            />
          </Box>
        )}
        <TestCycleTable
          data={formattedData || []}
          headerTabs={tableHeaderTestCycles}
          showCheckbox={canManageTestCycles}
          page={page}
          setPerPage={handlePageSizeChange}
          perPage={perPage}
          order={order}
          setOrder={setOrder}
          isLoading={isInitialLoading || isFetchingCycles}
          defaultSort="created_at"
          selected={selected}
          setSelected={setSelected}
          setSelectedRows={setSelectedRows}
          totalCount={totalCount}
          showPages
          striped
          handlePageChange={handlePageChange}
          tablekey="test cycles"
          showCount
          showRowCount={true}
          showColumnManager
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
          mandatoryKeys={['key', 'name']}
          defaultColumns={DEFAULT_COLUMNS}
          onRefresh={handleRefresh}
          serverSideSorting
          onSortChange={handleSortChange}
          action={(row) =>
            canManageTestCycles && (
              <ActionMenu
                items={[
                  {
                    label: 'Complete',
                    icon: <Check />,
                    disabled: row?.completion_date !== null,
                    onClick: () => handleMarkCompleteClick(row),
                  },
                  {
                    label: 'View Executions',
                    icon: <Rule />,
                    onClick: () =>
                      navigate(`/project/${projectId}/testcycles/${row.key}`),
                  },
                  {
                    label: 'Edit',
                    icon: <Edit />,
                    onClick: () => handleEdit(row),
                  },
                  {
                    label: 'Clone',
                    icon: <ContentCopy />,
                    onClick: () => handleClone(row),
                  },
                  {
                    label: 'Delete',
                    icon: <Delete />,
                    color: 'error',
                    onClick: () => handleDelete(row),
                    hidden: !canDeleteTestCycles,
                  },
                ]}
              />
            )
          }
        />
        <AddTestCycleModal
          showModal={showModal}
          handleClose={() => setShowModal(false)}
          fetchTestCycles={handleRefresh}
          addNotification={addNotification}
          statuses={config?.status}
          purposeOptions={config?.purpose}
          typeOptions={config?.type}
          jobs={jobs}
          jobError={jobError}
          projectUsers={projectUsers}
        />
        <FilterModal
          open={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          onApply={(newFilters) => { setFilters(newFilters); setPage(1); }}
          filterConfig={filterConfig}
          currentFilters={filters}
          optionsMap={optionsMap}
          title="Filter Test Cycles"
          showExecutionScope={isScopeDefined}
          ExecutionScopeComponent={isScopeDefined ? <ExecutionScopeForm isPlatformRequired={false} /> : null}
          savedFilters={savedFilters}
          refreshPresets={fetchFilterPresets}
          userRole={activeRole}
          projectId={projectId}
          filterType="testcycle"
          currentUserId={currentUser?.id}
        />
      </Container>

      <ConfirmationModal
        show={showFirstConfirm}
        buttonStyle="warning"
        handleConfirm={() => {
          setShowFirstConfirm(false);
          setShowSecondConfirm(true);
        }}
        handleClose={() => setShowFirstConfirm(false)}
        title="Complete Test Cycle"
        body={
          <div>
            <p>
              Once the test cycle is marked as completed, the following actions
              will no longer be possible:
            </p>
            <ul>
              <li>Edit the test cycle details</li>
              <li>Execute test cases within the cycle</li>
            </ul>
            <p>Do you want to continue?</p>
          </div>
        }
      />

      <ConfirmationModal
        show={showSecondConfirm}
        buttonStyle="error"
        handleConfirm={() => {
          handleCompleteTestCycle();
          setShowSecondConfirm(false);
        }}
        handleClose={() => setShowSecondConfirm(false)}
        title="Complete Test Cycle"
        body={
          <div>
            <p style={{ color: 'red' }}>
              This action is irreversible and cannot be undone.
            </p>
            <p>
              Once the test cycle is marked as completed, you will not be able
              to make any further changes or modifications.
            </p>
            <p>Are you absolutely sure you want to complete this test cycle?</p>
          </div>
        }
      />
    </>
  );
};

export default TestCyclesTabContentComponent;
